import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getShareableReport } from "../../../../services/dynamicActionPlanApi";
import SummaryReport from "./components/SummaryReport";
import AppReportToolbar from "../../../report/AppReportToolbar";
import "./styles.css";

const COPY = {
  en: {
    loading: ["Loading report…", "Please wait while the report is retrieved."],
    expired: [
      "This report has expired",
      "Shared action-plan reports are available for 30 days.",
    ],
    not_found: [
      "Report not found",
      "This link is invalid or the report is no longer available.",
    ],
    error: ["Could not load report", "Please try again later."],
    unsupported: [
      "Unsupported report version",
      "This report was created with a format this app cannot display.",
    ],
    title: "Dynamic Action Plan Report",
    footer:
      "This read-only report is available for 30 days from the date it was shared.",
  },
  ja: {
    loading: ["レポートを読み込み中…", "レポートを取得しています。"],
    expired: [
      "このレポートの有効期限が切れています",
      "共有されたアクションプランの有効期間は30日間です。",
    ],
    not_found: [
      "レポートが見つかりません",
      "リンクが無効か、レポートを利用できなくなっています。",
    ],
    error: [
      "レポートを読み込めませんでした",
      "しばらくしてから再試行してください。",
    ],
    unsupported: [
      "対応していないレポート形式です",
      "このレポートは、現在のアプリで表示できない形式で作成されています。",
    ],
    title: "動的アクションプランレポート",
    footer: "この読み取り専用レポートは、共有日から30日間閲覧できます。",
  },
  zh: {
    loading: ["正在加载报告…", "正在获取报告，请稍候。"],
    expired: ["此报告已过期", "共享的行动计划报告有效期为 30 天。"],
    not_found: ["未找到报告", "此链接无效，或该报告已不再可用。"],
    error: ["无法加载报告", "请稍后重试。"],
    unsupported: ["不支持的报告版本", "此报告的格式无法在当前应用中显示。"],
    title: "动态行动计划报告",
    footer: "此只读报告自共享之日起 30 天内可查看。",
  },
  es: {
    loading: ["Cargando informe…", "Espera mientras se recupera el informe."],
    expired: [
      "Este informe ha caducado",
      "Los informes de plan de acción compartidos están disponibles durante 30 días.",
    ],
    not_found: [
      "Informe no encontrado",
      "Este enlace no es válido o el informe ya no está disponible.",
    ],
    error: ["No se pudo cargar el informe", "Inténtalo de nuevo más tarde."],
    unsupported: [
      "Versión de informe no compatible",
      "Este informe se creó con un formato que esta app no puede mostrar.",
    ],
    title: "Informe del plan de acción dinámico",
    footer:
      "Este informe de solo lectura está disponible durante 30 días desde la fecha en que se compartió.",
  },
  fr: {
    loading: [
      "Chargement du rapport…",
      "Veuillez patienter pendant la récupération du rapport.",
    ],
    expired: [
      "Ce rapport a expiré",
      "Les rapports de plan d'action partagés sont disponibles pendant 30 jours.",
    ],
    not_found: [
      "Rapport introuvable",
      "Ce lien n'est pas valide ou le rapport n'est plus disponible.",
    ],
    error: [
      "Impossible de charger le rapport",
      "Veuillez réessayer plus tard.",
    ],
    unsupported: [
      "Version de rapport non prise en charge",
      "Ce rapport a été créé dans un format que cette application ne peut pas afficher.",
    ],
    title: "Rapport du plan d'action dynamique",
    footer:
      "Ce rapport en lecture seule est disponible pendant 30 jours à compter de sa date de partage.",
  },
  ru: {
    loading: ["Загрузка отчёта…", "Подождите, отчёт загружается."],
    expired: [
      "Срок действия отчёта истёк",
      "Общие отчёты по плану действий доступны в течение 30 дней.",
    ],
    not_found: [
      "Отчёт не найден",
      "Ссылка недействительна или отчёт больше недоступен.",
    ],
    error: ["Не удалось загрузить отчёт", "Повторите попытку позже."],
    unsupported: [
      "Неподдерживаемая версия отчёта",
      "Отчёт создан в формате, который это приложение не может отобразить.",
    ],
    title: "Отчёт по динамическому плану действий",
    footer:
      "Этот отчёт только для чтения доступен в течение 30 дней с даты, когда им поделились.",
  },
};

const normalizeReportLocale = (locale) =>
  Object.prototype.hasOwnProperty.call(COPY, locale) ? locale : "en";

const SharedActionPlanReport = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ status: "loading", report: null });
  const locale = normalizeReportLocale(
    state.report?.data?.locale || searchParams.get("locale"),
  );
  const copy = COPY[locale];

  useEffect(() => {
    let active = true;
    setState({ status: "loading", report: null });
    getShareableReport(slug)
      .then((report) => {
        if (active) setState({ status: "ok", report });
      })
      .catch((error) => {
        if (!active) return;
        if (error?.response?.status === 410) {
          setState({ status: "expired", report: null });
        } else if (error?.response?.status === 404) {
          setState({ status: "not_found", report: null });
        } else {
          setState({ status: "error", report: null });
        }
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (state.status !== "ok") {
    const statusCopy = copy[state.status];
    return (
      <main className="dap-public-report-state" lang={locale}>
        <AppReportToolbar />
        <div className="dap-public-brand">SeniorLife.AI</div>
        <section role={state.status === "loading" ? "status" : "alert"}>
          <h1>{statusCopy[0]}</h1>
          <p>{statusCopy[1]}</p>
        </section>
      </main>
    );
  }

  const { report } = state;
  if (
    report?.version !== "v1" ||
    report?.data?.kind !== "dynamic_action_plan"
  ) {
    return (
      <main className="dap-public-report-state" lang={locale}>
        <AppReportToolbar />
        <div className="dap-public-brand">SeniorLife.AI</div>
        <section role="alert">
          <h1>{copy.unsupported[0]}</h1>
          <p>{copy.unsupported[1]}</p>
        </section>
      </main>
    );
  }

  const summary = {
    ...(report.data.summary || {}),
    generatedAt: report.createdAt || report.data.createdAt,
  };

  return (
    <main className="dap-public-report-page" lang={locale}>
      <AppReportToolbar />
      <header>
        <strong>SeniorLife.AI</strong>
        <span>
          {report.data.tenantType === "pharmacy" && summary.title
            ? summary.title
            : copy.title}
        </span>
      </header>
      <article>
        <SummaryReport
          summary={summary}
          locale={locale}
          showReportLink={report.data.tenantType === "pharmacy"}
        />
      </article>
      <footer>{copy.footer}</footer>
    </main>
  );
};

export default SharedActionPlanReport;
